/**
 * TypewriterText: STT/번역 텍스트를 빠르게 한 글자씩 타이핑하듯 표시.
 *
 * Jigasi interim 결과는 어절/문장 단위로 한꺼번에 갱신되므로
 * ("안녕하세요. 저는" → "안녕하세요. 저는 손영한 선임 연구원입니다.")
 * 그대로 렌더하면 텍스트가 통째로 바뀌어 보인다. 기존 표시 텍스트와 새 텍스트의
 * 공통 prefix는 유지하고, 새로 추가된 부분만 빠르게 타이핑해 스트리밍처럼 보이게 한다.
 *
 * mode:
 * - 'retype' (원문 STT용, 기본): 인식 결과가 중간에 수정(rewrite)되면
 *   단어 단위로 느슨하게 정렬해서, 이미 표시된 단어와 사실상 같은 선두 단어들
 *   (대소문자/구두점 차이, 한 글자 교정, 어간 확장)은 교정된 형태로 즉시 반영하고
 *   그 뒤부터 이어서 타이핑 — 영어 STT처럼 페이즈마다 "hello i am" →
 *   "Hello, I am a senior…"로 전체를 다시 정규화해 보내는 경우에도
 *   전체 재타이핑 없이 뒤에 이어 붙는 것처럼 보인다.
 *   느슨한 일치가 전혀 없으면(완전히 다른 문장) 비우고 다시 타이핑.
 * - 'append' (번역문용): 확장(append)될 때만 타이핑.
 *   재번역으로 어순이 통째로 바뀌면 즉시 새 텍스트로 교체 (긴 문장 재타이핑 방지)
 *
 * animKey 레인별로 마지막 표시 텍스트를 모듈 레벨에 기억해서
 * interim(currentTexts) → final(entry)로 컴포넌트가 교체(unmount/remount)되어도
 * 끊기지 않고 이어서 타이핑한다.
 *
 * laneWrite: 레인의 쓰기 권한. interim(진행 중) 아이템만 레인에 기록하고(true, 기본),
 * final entry는 마운트 시 읽기만 한다(false). final-1이 아직 꼬리를 타이핑 중일 때
 * 다음 발화의 interim/final이 연달아 도착해도, 두 애니메이션이 같은 레인을
 * 번갈아 덮어써서 seed가 오염되는 것을 방지한다.
 *
 * enabled=false면 애니메이션 없이 그대로 렌더 (과거 히스토리, 스크롤백 재마운트).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type TypewriterMode = 'retype' | 'append';

interface TypewriterOptions {
  enabled?: boolean;
  mode?: TypewriterMode;
  laneWrite?: boolean;
}

/** 타이핑 최저 속도 (글자/초) */
const MIN_CHARS_PER_SEC = 45;
/** 남은 글자가 많아도 이 시간 안에는 목표 텍스트를 따라잡도록 속도를 높인다 */
const MAX_CATCHUP_MS = 600;
/** 탭 비활성 등으로 프레임 간격이 벌어져도 dt는 이 값으로 제한 */
const MAX_FRAME_DELTA_MS = 100;
/** final entry가 이 시간 안에 렌더되면 "방금 확정된 발화"로 보고 타이핑 대상에 포함 */
export const TYPEWRITER_FRESH_WINDOW_MS = 5000;

/** animKey 레인별 마지막 표시 텍스트. interim → final 전환 시 이어치기용 */
const lastDisplayedByLane = new Map<string, string>();
const LANE_REGISTRY_MAX = 100;

/** 방금 도착한 final entry인지 (스크롤백으로 재마운트된 과거 entry 제외) */
export const isFreshCaptionTimestamp = (timestamp: number): boolean =>
  Date.now() - timestamp < TYPEWRITER_FRESH_WINDOW_MS;

const commonPrefixLength = (a: string, b: string): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i += 1;
  return i;
};

const saveLane = (lane: string, text: string) => {
  if (!lastDisplayedByLane.has(lane) && lastDisplayedByLane.size >= LANE_REGISTRY_MAX) {
    const oldest = lastDisplayedByLane.keys().next().value;
    if (oldest !== undefined) lastDisplayedByLane.delete(oldest);
  }
  lastDisplayedByLane.set(lane, text);
};

// ── 단어 단위 느슨한 정렬 (retype 모드 rewrite 처리) ─────────────────────────
// 영어 STT는 페이즈마다 대소문자/구두점을 전체 재정규화해서 보내므로
// ("hello i am" → "Hello, I am a senior…") 문자 단위 공통 prefix가 0이 된다.
// 이미 표시된 단어와 "사실상 같은" 새 텍스트의 선두 단어들을 찾아,
// 교정된 형태로 즉시 반영하고 그 뒤부터 타이핑을 이어가기 위한 헬퍼.

/** 대소문자/구두점 차이를 무시한 단어 비교용 정규화 */
const normalizeWord = (word: string): string =>
  word.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** 두 문자열이 삽입/삭제/치환 1회 이내로 같은지 (Levenshtein ≤ 1) */
const isWithinOneEdit = (a: string, b: string): boolean => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0;
  while (i < shorter.length && shorter[i] === longer[i]) i += 1;
  if (shorter.length === longer.length) return shorter.slice(i + 1) === longer.slice(i + 1);
  return shorter.slice(i) === longer.slice(i + 1);
};

/** STT 재인식 관점에서 "사실상 같은 단어"인지 (교정으로 봐도 되는 차이인지) */
const isSameWordLoosely = (a: string, b: string): boolean => {
  const na = normalizeWord(a);
  const nb = normalizeWord(b);
  if (na === nb) return na.length > 0 || a === b; // 둘 다 순수 기호면 원문으로 비교
  if (Math.min(na.length, nb.length) < 2) return false;
  if (isWithinOneEdit(na, nb)) return true; // 한 글자 교정 (재는 → 저는)
  // 어간 확장 (want → wanted, sen → senior)
  const [shorter, longer] = na.length <= nb.length ? [na, nb] : [nb, na];
  return longer.startsWith(shorter) && longer.length - shorter.length <= 3;
};

/**
 * rewrite된 새 텍스트에서 타이핑을 이어갈 시작 오프셋을 계산한다.
 * 표시 중인 단어들과 새 텍스트의 선두 단어들을 순서대로 느슨하게 매칭해서,
 * 마지막으로 매칭된 단어의 끝(새 텍스트 기준)을 돌려준다.
 * 매칭된 구간은 교정된 형태로 즉시 표시되고, 그 뒤부터 타이핑된다.
 * 전혀 매칭되지 않으면 0 (비우고 처음부터 재타이핑).
 */
export const alignResumeOffset = (displayed: string, next: string): number => {
  const shown = displayed.split(/\s+/).filter((w) => w.length > 0);
  if (shown.length === 0) return 0;

  const spans: Array<{ word: string; start: number; end: number }> = [];
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(next)) !== null) {
    spans.push({ word: m[0], start: m.index, end: m.index + m[0].length });
  }

  let resume = 0;
  const count = Math.min(shown.length, spans.length);
  for (let i = 0; i < count; i += 1) {
    const isLastShown = i === shown.length - 1;
    // 타이핑 중이던 마지막 단어가 새 단어의 앞부분 그대로면 단어 안에서 이어서 타이핑
    if (isLastShown && spans[i].word.length > shown[i].length && spans[i].word.startsWith(shown[i])) {
      resume = spans[i].start + shown[i].length;
      break;
    }
    if (!isSameWordLoosely(shown[i], spans[i].word)) break;
    resume = spans[i].end;
  }
  return resume;
};

export const useTypewriterText = (
  text: string,
  animKey: string,
  { enabled = true, mode = 'retype', laneWrite = true }: TypewriterOptions = {},
): string => {
  const [displayed, setDisplayed] = useState(() => {
    if (!enabled) return text;
    // 같은 레인에서 직전에 표시하던 텍스트와 겹치는 부분부터 이어서 타이핑.
    // 문자 prefix 외에 단어 정렬도 적용 — final에서 대소문자/구두점이
    // 재정규화되어도("hello i am" → "Hello, I am…") 교정된 형태로 이어간다
    const last = lastDisplayedByLane.get(animKey) ?? '';
    const resume = Math.max(commonPrefixLength(last, text), alignResumeOffset(last, text));
    return text.slice(0, resume);
  });

  const displayedRef = useRef(displayed);
  const targetRef = useRef(text);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef(0);
  const carryRef = useRef(0);
  /** 타이핑 속도(글자/초). 목표 텍스트가 바뀔 때마다 남은 분량 기준으로 고정 */
  const rateRef = useRef(MIN_CHARS_PER_SEC);

  const applyDisplayed = useCallback((next: string) => {
    displayedRef.current = next;
    if (laneWrite) saveLane(animKey, next);
    setDisplayed(next);
  }, [animKey, laneWrite]);

  const stopLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastTsRef.current = 0;
    carryRef.current = 0;
  }, []);

  const step = useCallback((ts: number) => {
    const target = targetRef.current;
    const current = displayedRef.current;
    if (current.length >= target.length) {
      rafRef.current = null;
      lastTsRef.current = 0;
      carryRef.current = 0;
      return;
    }

    const dt = lastTsRef.current ? Math.min(ts - lastTsRef.current, MAX_FRAME_DELTA_MS) : 1000 / 60;
    lastTsRef.current = ts;

    carryRef.current += (rateRef.current * dt) / 1000;
    const charsToAdd = Math.floor(carryRef.current);
    if (charsToAdd > 0) {
      carryRef.current -= charsToAdd;
      applyDisplayed(target.slice(0, current.length + charsToAdd));
    }

    if (displayedRef.current.length >= target.length) {
      rafRef.current = null;
      lastTsRef.current = 0;
      carryRef.current = 0;
      return;
    }
    rafRef.current = requestAnimationFrame(step);
  }, [applyDisplayed]);

  const startLoop = useCallback(() => {
    if (rafRef.current !== null) return;
    lastTsRef.current = 0;
    rafRef.current = requestAnimationFrame(step);
  }, [step]);

  useEffect(() => {
    targetRef.current = text;

    if (!enabled) {
      // 히스토리/스크롤백: 즉시 렌더. 라이브 레인을 과거 텍스트로 오염시키지 않도록 saveLane 생략
      stopLoop();
      if (displayedRef.current !== text) {
        displayedRef.current = text;
        setDisplayed(text);
      }
      return;
    }

    const current = displayedRef.current;
    if (current === text) return;

    const prefixLen = commonPrefixLength(current, text);
    if (prefixLen < current.length) {
      // 이미 표시한 내용이 수정(rewrite)된 경우
      if (mode === 'append') {
        stopLoop();
        applyDisplayed(text);
        return;
      }
      // 단어 단위 정렬: 표시된 단어와 사실상 같은 새 텍스트의 선두 단어들은
      // 교정된 형태로 즉시 반영하고 그 뒤부터 이어서 타이핑.
      // 문자 단위 공통 prefix가 더 길면 그쪽을 사용 (부분 단어 유지)
      const resume = Math.max(prefixLen, alignResumeOffset(current, text));
      applyDisplayed(text.slice(0, resume));
    }
    // 남은 분량이 많아도 MAX_CATCHUP_MS 안에 완료되도록 속도를 고정
    rateRef.current = Math.max(
      MIN_CHARS_PER_SEC,
      ((text.length - displayedRef.current.length) * 1000) / MAX_CATCHUP_MS,
    );
    startLoop();
  }, [text, enabled, mode, applyDisplayed, startLoop, stopLoop]);

  useEffect(() => () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  return displayed;
};

interface TypewriterTextProps extends TypewriterOptions {
  text: string;
  /** 타이핑 이어치기 레인 키. 표시 위치(surface)별 prefix + 화자 id 조합으로 유일해야 한다 */
  animKey: string;
}

const TypewriterText: React.FC<TypewriterTextProps> = ({ text, animKey, enabled, mode, laneWrite }) => {
  const displayed = useTypewriterText(text, animKey, { enabled, mode, laneWrite });
  // 완전히 다른 문장으로 rewrite되어 표시 텍스트가 순간 비어도 줄 높이가 무너지지 않게 ZWSP 유지
  return <>{displayed.length > 0 ? displayed : '\u200B'}</>;
};

export default TypewriterText;

/**
 * InterimBubble: 진행 중(partial) 자막 말풍선.
 *
 * partial 인식 결과가 통째로 rewrite되면 타이핑이 처음부터 다시 시작되어
 * 3줄 → 1줄 → 2줄 → 3줄로 높이가 오르내린다. 이미 도달한 최대 높이를
 * min-height 바닥값으로 잡아, 문장이 전부 바뀌어도 말풍선이 줄어들지 않게 한다.
 *
 * final은 partial 아이템이 사라지고 별도 entry로 새로 마운트되므로
 * 바닥값 없이 자기 높이로 렌더된다 (final이 더 짧으면 그만큼 줄어든다).
 */
export const InterimBubble: React.FC<{
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ className, style, children }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [minHeight, setMinHeight] = useState(0);

  // 렌더마다 측정. min-height가 이미 걸려 있으므로 offsetHeight는 항상 바닥값 이상이고,
  // 커질 때만 갱신하므로 재렌더가 반복되지 않는다.
  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? 0;
    if (height > minHeight) setMinHeight(height);
  });

  return (
    <p
      ref={ref}
      className={className}
      // padding이 있는 말풍선에서도 offsetHeight와 min-height 기준을 맞춘다
      style={minHeight > 0 ? { ...style, boxSizing: 'border-box', minHeight } : style}
    >
      {children}
    </p>
  );
};
